import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import {
  Loader2,
  Sparkles,
  Download,
  RefreshCw,
  HelpCircle,
  RotateCcw,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Undo2,
  Redo2,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

// Format number with commas for display
const formatNumberWithCommas = (value: string): string => {
  if (!value) return value;
  // Remove existing commas and non-numeric chars except decimal
  const cleaned = value.replace(/[^\d.]/g, '');
  // Split on decimal point
  const parts = cleaned.split('.');
  // Add commas to integer part
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Return formatted number (max 2 decimal places)
  return parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 2)}` : parts[0];
};

// Parse number from formatted string
const parseFormattedNumber = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/[^\d.]/g, '')) || 0;
};

// Validation thresholds
const VALIDATION_THRESHOLDS = {
  arr: { min: 10000, label: 'ARR', warning: 'ARR below $10,000 seems unusually low' },
  tam: { min: 100000, label: 'TAM', warning: 'TAM below $100,000 seems unusually low' },
  sam: { min: 50000, label: 'SAM', warning: 'SAM below $50,000 seems unusually low' },
  som: { min: 10000, label: 'SOM', warning: 'SOM below $10,000 seems unusually low' },
  cash: { min: 1000, label: 'Cash', warning: 'Cash below $1,000 seems unusually low' },
  burn: { min: 0, label: 'Monthly Burn', warning: 'Monthly burn cannot be negative' },
};

// Validate numeric inputs
const validateNumber = (value: string, field: string): string | null => {
  if (!value) return null;
  const numValue = parseFormattedNumber(value);
  const threshold = VALIDATION_THRESHOLDS[field as keyof typeof VALIDATION_THRESHOLDS];
  if (threshold && numValue < threshold.min) {
    return threshold.warning;
  }
  return null;
};

// Validate percentage inputs
const validatePercentage = (value: string, label: string): string | null => {
  if (!value) return null;
  const numValue = parseFloat(value);
  if (numValue > 100) {
    return `${label} cannot exceed 100%`;
  }
  if (numValue < 0) {
    return `${label} cannot be negative`;
  }
  return null;
};

// Handle number input change
const handleNumberInput = (
  value: string,
  field: string,
  setter: (fn: (prev: any) => any) => void,
  setWarnings: (fn: (prev: any) => any) => void,
  saveHistory?: () => void
) => {
  const formatted = formatNumberWithCommas(value);
  setter((prev: any) => ({ ...prev, [field]: formatted }));

  // Validate and update warning state
  const warning = validateNumber(formatted, field);
  setWarnings((prev: any) => {
    if (warning) {
      return { ...prev, [field]: warning };
    } else {
      const newWarnings = { ...prev };
      delete newWarnings[field];
      return newWarnings;
    }
  });

  // Show toast for new warnings
  if (warning) {
    setTimeout(() => {
      toast({
        title: 'Validation Warning',
        description: warning,
        variant: 'default',
      });
    }, 800);
  }

  // Save to history after a delay (debounced)
  if (saveHistory) {
    setTimeout(() => saveHistory(), 500);
  }
};

// Handle percentage input change
const handlePercentageInput = (
  value: string,
  field: string,
  label: string,
  setter: (fn: (prev: any) => any) => void,
  setWarnings: (fn: (prev: any) => any) => void,
  saveHistory?: () => void
) => {
  setter((prev: any) => ({ ...prev, [field]: value }));

  // Validate and update warning state
  const warning = validatePercentage(value, label);
  setWarnings((prev: any) => {
    if (warning) {
      return { ...prev, [field]: warning };
    } else {
      const newWarnings = { ...prev };
      delete newWarnings[field];
      return newWarnings;
    }
  });

  // Show toast for new warnings
  if (warning) {
    setTimeout(() => {
      toast({
        title: 'Validation Warning',
        description: warning,
        variant: 'default',
      });
    }, 800);
  }

  // Save to history after a delay (debounced)
  if (saveHistory) {
    setTimeout(() => saveHistory(), 500);
  }
};

export default function ValuationAdvisor({
  companyId,
  base = 'USD',
  roundId,
}: {
  companyId: string;
  base?: string;
  roundId?: string;
}) {
  const [tab, setTab] = useState('inputs');
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState<Record<string, string>>({});
  const [showWarningPanel, setShowWarningPanel] = useState(true);

  // Undo/Redo state management
  const [history, setHistory] = useState<Array<{ inputs: any; offer: any }>>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const maxHistorySize = 10;

  // Check if there are any critical validation warnings
  const warningCount = Object.keys(validationWarnings).length;
  const hasCriticalWarnings = warningCount > 0;

  // Check if there are validation warnings in offer fields
  const hasOfferWarnings = ['offered_pre_money', 'raise_amount', 'pool_target_pct'].some(
    (field) => validationWarnings[field]
  );

  // Undo/Redo availability
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Field labels for better display
  const fieldLabels: Record<string, string> = {
    arr: 'ARR',
    growth_pct: 'YoY Growth %',
    gross_margin_pct: 'Gross Margin %',
    churn_pct: 'Churn %',
    tam: 'Total Addressable Market',
    sam: 'Serviceable Addressable Market',
    som: 'Serviceable Obtainable Market',
    cash: 'Cash on Hand',
    burn: 'Monthly Burn',
    dcf_discount_pct: 'DCF Discount Rate %',
    offered_pre_money: 'Offered Pre-Money',
    raise_amount: 'Raise Amount',
    pool_target_pct: 'Option Pool %',
  };

  // Inputs (founder-editable; prefilled from docs/round)
  const [inputs, setInputs] = useState<any>({
    stage: 'SEED',
    sector: 'SaaS',
    arr: '',
    growth_pct: '',
    gross_margin_pct: '',
    churn_pct: '',
    tam: '',
    sam: '',
    som: '',
    burn: '',
    cash: '',
    dcf_years: 5,
    dcf_discount_pct: 25,
    model_mid: '',
  });

  // Offer (optional)
  const [offer, setOffer] = useState<any>({
    offered_pre_money: '',
    raise_amount: '',
    pool_target_pct: '10',
    instrument: 'EQUITY',
  });

  // Save state to history
  const saveToHistory = () => {
    const newState = { inputs: { ...inputs }, offer: { ...offer } };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);

    // Keep only last 10 states
    if (newHistory.length > maxHistorySize) {
      newHistory.shift();
    } else {
      setHistoryIndex((prev) => prev + 1);
    }

    setHistory(newHistory);
  };

  // Undo function
  const handleUndo = () => {
    if (!canUndo) return;

    const prevIndex = historyIndex - 1;
    const prevState = history[prevIndex];

    setInputs(prevState.inputs);
    setOffer(prevState.offer);
    setHistoryIndex(prevIndex);

    // Re-validate
    setValidationWarnings({});

    toast({ title: 'Undo applied', description: 'Reverted to previous state' });
  };

  // Redo function
  const handleRedo = () => {
    if (!canRedo) return;

    const nextIndex = historyIndex + 1;
    const nextState = history[nextIndex];

    setInputs(nextState.inputs);
    setOffer(nextState.offer);
    setHistoryIndex(nextIndex);

    // Re-validate
    setValidationWarnings({});

    toast({ title: 'Redo applied', description: 'Moved forward in history' });
  };

  // Initialize history on mount
  useEffect(() => {
    const initialState = { inputs: { ...inputs }, offer: { ...offer } };
    setHistory([initialState]);
    setHistoryIndex(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Results
  const [result, setResult] = useState<any>(null);
  const [compare, setCompare] = useState<any>(null);
  const [brief, setBrief] = useState<any>(null);

  async function prefill() {
    setPrefilling(true);
    try {
      const { data: fromDocs } = await supabase.rpc('va_prefill_from_docs', {
        company_id: companyId,
      });
      const { data: fromRound } = roundId
        ? await supabase.rpc('va_prefill_from_round', { round_id: roundId })
        : { data: null };

      if (fromDocs && typeof fromDocs === 'object' && 'metrics' in fromDocs) {
        const metrics = fromDocs.metrics as any;
        // Format numeric fields when prefilling
        const formattedMetrics = Object.entries(metrics).reduce((acc, [key, value]) => {
          if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
            acc[key] = formatNumberWithCommas(String(value));
          } else {
            acc[key] = value;
          }
          return acc;
        }, {} as any);

        setInputs((i: any) => ({
          ...i,
          ...formattedMetrics,
        }));
      }

      if (fromRound && typeof fromRound === 'object') {
        const roundData = fromRound as any;
        setOffer((o: any) => ({
          ...o,
          offered_pre_money: roundData.pre_money
            ? formatNumberWithCommas(String(roundData.pre_money))
            : o.offered_pre_money,
          raise_amount: roundData.target_amount
            ? formatNumberWithCommas(String(roundData.target_amount))
            : o.raise_amount,
          pool_target_pct: String(roundData.option_pool_increase ?? o.pool_target_pct),
          instrument: roundData.instrument || o.instrument,
        }));
      }
      toast({ title: 'Prefilled from docs/round' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setPrefilling(false);
    }
  }

  async function runModels() {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke('va-run-models', {
        body: { companyId, base, inputs },
      });
      if (error) throw error;
      setResult(data);
      setInputs((i: any) => ({
        ...i,
        model_mid: data.range?.mid ? formatNumberWithCommas(String(data.range.mid)) : '',
      }));
      toast({ title: 'Valuation models complete' });
      setTab('methods');
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  }

  async function compareOffer() {
    if (!result) {
      toast({ title: 'Run models first', variant: 'destructive' });
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('va-offer-compare', {
        body: { companyId, base, inputs, offer },
      });
      if (error) throw error;
      setCompare(data);
      setTab('offer');
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  }

  async function negotiationBrief() {
    try {
      const { data, error } = await supabase.functions.invoke('va-negotiation-brief', {
        body: { companyId, base, inputs, offer },
      });
      if (error) throw error;
      setBrief(data);
      setTab('negotiation');
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  }

  async function exportBrief() {
    try {
      const { data, error } = await supabase.functions.invoke('va-export-brief-pdf', {
        body: { companyId, base, inputs, offer },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, '_blank');
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  }

  function resetAll() {
    // Reset inputs to defaults
    setInputs({
      stage: 'SEED',
      sector: 'SaaS',
      arr: '',
      growth_pct: '',
      gross_margin_pct: '',
      churn_pct: '',
      tam: '',
      sam: '',
      som: '',
      burn: '',
      cash: '',
      dcf_years: 5,
      dcf_discount_pct: 25,
      model_mid: '',
    });

    // Reset offer to defaults
    setOffer({
      offered_pre_money: '',
      raise_amount: '',
      pool_target_pct: '10',
      instrument: 'EQUITY',
    });

    // Clear all results
    setResult(null);
    setCompare(null);
    setBrief(null);

    // Clear validation warnings
    setValidationWarnings({});

    // Return to inputs tab
    setTab('inputs');

    toast({ title: 'Dashboard cleared', description: 'Ready to run a new valuation model' });
  }

  function clearWarnings() {
    // Reset only fields that have validation warnings
    const warningFields = Object.keys(validationWarnings);

    if (warningFields.length === 0) return;

    // Clear input fields with warnings
    setInputs((prev: any) => {
      const updated = { ...prev };
      warningFields.forEach((field) => {
        if (field in updated) {
          updated[field] = '';
        }
      });
      return updated;
    });

    // Clear offer fields with warnings
    setOffer((prev: any) => {
      const updated = { ...prev };
      warningFields.forEach((field) => {
        if (field in updated) {
          updated[field] = '';
        }
      });
      return updated;
    });

    // Clear validation warnings
    setValidationWarnings({});

    toast({
      title: 'Warnings cleared',
      description: `${warningFields.length} field${warningFields.length > 1 ? 's' : ''} reset to empty`,
    });
  }

  function autoFixWarnings() {
    const warningFields = Object.keys(validationWarnings);

    if (warningFields.length === 0) return;

    // Save current state to history before auto-fixing
    saveToHistory();

    let fixedCount = 0;

    // Auto-fix input fields with warnings
    setInputs((prev: any) => {
      const updated = { ...prev };
      warningFields.forEach((field) => {
        if (field in updated) {
          const value = updated[field];
          const warning = validationWarnings[field];

          // Fix percentages over 100%
          if (warning?.includes('cannot exceed 100%')) {
            updated[field] = '100';
            fixedCount++;
          }
          // Fix negative percentages
          else if (warning?.includes('cannot be negative') && field.includes('pct')) {
            updated[field] = '0';
            fixedCount++;
          }
          // Fix negative burn
          else if (field === 'burn' && warning?.includes('cannot be negative')) {
            updated[field] = '0';
            fixedCount++;
          }
          // For low value warnings, set to minimum threshold
          else if (warning?.includes('unusually low')) {
            const thresholds: Record<string, number> = {
              arr: 10000,
              tam: 100000,
              sam: 50000,
              som: 10000,
              cash: 1000,
              burn: 0,
            };
            if (field in thresholds) {
              updated[field] = formatNumberWithCommas(String(thresholds[field]));
              fixedCount++;
            }
          }
        }
      });
      return updated;
    });

    // Auto-fix offer fields with warnings
    setOffer((prev: any) => {
      const updated = { ...prev };
      warningFields.forEach((field) => {
        if (field in updated) {
          const warning = validationWarnings[field];

          // Fix percentages over 100%
          if (warning?.includes('cannot exceed 100%')) {
            updated[field] = '100';
            fixedCount++;
          }
          // Fix negative percentages
          else if (warning?.includes('cannot be negative')) {
            updated[field] = '0';
            fixedCount++;
          }
        }
      });
      return updated;
    });

    // Clear validation warnings
    setValidationWarnings({});

    toast({
      title: 'Auto-fix applied',
      description: `${fixedCount} field${fixedCount > 1 ? 's' : ''} corrected to valid values`,
    });
  }

  useEffect(() => {
    prefill().finally(() => setLoading(false));
  }, [companyId, roundId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Ella Valuation Advisor</CardTitle>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleUndo}
                disabled={!canUndo}
                className={cn(!canUndo && 'opacity-50 cursor-not-allowed')}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{canUndo ? 'Undo changes' : 'No history to undo'}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRedo}
                disabled={!canRedo}
                className={cn(!canRedo && 'opacity-50 cursor-not-allowed')}
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{canRedo ? 'Redo changes' : 'No history to redo'}</TooltipContent>
          </Tooltip>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <RotateCcw className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will reset all inputs, clear all results, and return you to the inputs tab.
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={resetAll}>Clear All</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {hasCriticalWarnings && (
            <>
              <Button variant="default" size="sm" onClick={autoFixWarnings}>
                <Sparkles className="h-4 w-4 mr-1" />
                Auto-fix ({warningCount})
              </Button>
              <Button variant="outline" size="sm" onClick={clearWarnings}>
                <AlertTriangle className="h-4 w-4 mr-1" />
                Clear Warnings ({warningCount})
              </Button>
            </>
          )}

          <Button variant="outline" size="sm" onClick={prefill} disabled={prefilling}>
            {prefilling ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            Prefill
          </Button>
          <Button size="sm" onClick={exportBrief}>
            <Download className="h-4 w-4 mr-1" /> Export Brief
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {hasCriticalWarnings && (
          <Collapsible open={showWarningPanel} onOpenChange={setShowWarningPanel}>
            <div className="border border-yellow-500 rounded-lg bg-yellow-50 dark:bg-yellow-950/20">
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-yellow-100 dark:hover:bg-yellow-950/40 transition-colors">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <span className="font-medium text-yellow-900 dark:text-yellow-100">
                    {warningCount} Validation Warning{warningCount > 1 ? 's' : ''}
                  </span>
                </div>
                {showWarningPanel ? (
                  <ChevronUp className="h-4 w-4 text-yellow-600" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-yellow-600" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-3 pb-3 space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      Please fix the following issues before running models:
                    </p>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={autoFixWarnings}
                      className="h-7 text-xs"
                    >
                      <Sparkles className="h-3 w-3 mr-1" />
                      Auto-fix All
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(validationWarnings).map(([field, warning]) => (
                      <div
                        key={field}
                        className="flex items-start gap-2 text-sm bg-white dark:bg-gray-900 rounded p-2"
                      >
                        <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium text-yellow-900 dark:text-yellow-100">
                            {fieldLabels[field] || field}:
                          </span>
                          <span className="text-yellow-800 dark:text-yellow-200 ml-1">
                            {warning}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-3">
            <TabsTrigger value="inputs">Inputs</TabsTrigger>
            <TabsTrigger value="methods">Methods</TabsTrigger>
            <TabsTrigger value="offer">Offer Compare</TabsTrigger>
            <TabsTrigger value="negotiation">Negotiation</TabsTrigger>
          </TabsList>

          {/* Inputs */}
          <TabsContent value="inputs">
            <TooltipProvider>
              <div className="grid sm:grid-cols-3 gap-3">
                <Select
                  value={inputs.stage}
                  onValueChange={(v) => setInputs({ ...inputs, stage: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRE-SEED">Pre-Seed</SelectItem>
                    <SelectItem value="SEED">Seed</SelectItem>
                    <SelectItem value="SERIES_A">Series A</SelectItem>
                    <SelectItem value="SERIES_B">Series B</SelectItem>
                  </SelectContent>
                </Select>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="Sector (e.g., SaaS)"
                        value={inputs.sector}
                        onChange={(e) => setInputs({ ...inputs, sector: e.target.value })}
                      />
                      <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Industry sector (e.g., SaaS, Fintech, Healthcare)</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="ARR"
                        value={inputs.arr}
                        onChange={(e) =>
                          handleNumberInput(
                            e.target.value,
                            'arr',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.arr &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      {validationWarnings.arr ? (
                        <AlertTriangle className="absolute right-3 top-3 h-4 w-4 text-yellow-500" />
                      ) : (
                        <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {validationWarnings.arr ? (
                      <p className="text-yellow-500 font-medium">{validationWarnings.arr}</p>
                    ) : (
                      <p>Annual Recurring Revenue - your current yearly revenue</p>
                    )}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="YoY Growth %"
                        value={inputs.growth_pct}
                        onChange={(e) =>
                          handlePercentageInput(
                            e.target.value,
                            'growth_pct',
                            'Growth',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.growth_pct &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      {validationWarnings.growth_pct ? (
                        <AlertTriangle className="absolute right-3 top-3 h-4 w-4 text-yellow-500" />
                      ) : (
                        <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {validationWarnings.growth_pct ? (
                      <p className="text-yellow-500 font-medium">{validationWarnings.growth_pct}</p>
                    ) : (
                      <p>Year-over-year revenue growth percentage</p>
                    )}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="Gross Margin %"
                        value={inputs.gross_margin_pct}
                        onChange={(e) =>
                          handlePercentageInput(
                            e.target.value,
                            'gross_margin_pct',
                            'Gross Margin',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.gross_margin_pct &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      {validationWarnings.gross_margin_pct ? (
                        <AlertTriangle className="absolute right-3 top-3 h-4 w-4 text-yellow-500" />
                      ) : (
                        <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {validationWarnings.gross_margin_pct ? (
                      <p className="text-yellow-500 font-medium">
                        {validationWarnings.gross_margin_pct}
                      </p>
                    ) : (
                      <p>Percentage of revenue after cost of goods sold</p>
                    )}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="Churn %"
                        value={inputs.churn_pct}
                        onChange={(e) =>
                          handlePercentageInput(
                            e.target.value,
                            'churn_pct',
                            'Churn',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.churn_pct &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      {validationWarnings.churn_pct ? (
                        <AlertTriangle className="absolute right-3 top-3 h-4 w-4 text-yellow-500" />
                      ) : (
                        <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {validationWarnings.churn_pct ? (
                      <p className="text-yellow-500 font-medium">{validationWarnings.churn_pct}</p>
                    ) : (
                      <p>Monthly or annual customer churn rate</p>
                    )}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="Total Addressable Market"
                        value={inputs.tam}
                        onChange={(e) =>
                          handleNumberInput(
                            e.target.value,
                            'tam',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.tam &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      {validationWarnings.tam ? (
                        <AlertTriangle className="absolute right-3 top-3 h-4 w-4 text-yellow-500" />
                      ) : (
                        <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {validationWarnings.tam ? (
                      <p className="text-yellow-500 font-medium">{validationWarnings.tam}</p>
                    ) : (
                      <p>Total market demand for your product/service</p>
                    )}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="Serviceable Addressable Market"
                        value={inputs.sam}
                        onChange={(e) =>
                          handleNumberInput(
                            e.target.value,
                            'sam',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.sam &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      {validationWarnings.sam ? (
                        <AlertTriangle className="absolute right-3 top-3 h-4 w-4 text-yellow-500" />
                      ) : (
                        <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {validationWarnings.sam ? (
                      <p className="text-yellow-500 font-medium">{validationWarnings.sam}</p>
                    ) : (
                      <p>Portion of TAM you can reach with your sales channels</p>
                    )}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="Serviceable Obtainable Market"
                        value={inputs.som}
                        onChange={(e) =>
                          handleNumberInput(
                            e.target.value,
                            'som',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.som &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      {validationWarnings.som ? (
                        <AlertTriangle className="absolute right-3 top-3 h-4 w-4 text-yellow-500" />
                      ) : (
                        <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {validationWarnings.som ? (
                      <p className="text-yellow-500 font-medium">{validationWarnings.som}</p>
                    ) : (
                      <p>Realistic market share you can capture short-term</p>
                    )}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="Cash on Hand"
                        value={inputs.cash}
                        onChange={(e) =>
                          handleNumberInput(
                            e.target.value,
                            'cash',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.cash &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      {validationWarnings.cash ? (
                        <AlertTriangle className="absolute right-3 top-3 h-4 w-4 text-yellow-500" />
                      ) : (
                        <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {validationWarnings.cash ? (
                      <p className="text-yellow-500 font-medium">{validationWarnings.cash}</p>
                    ) : (
                      <p>Current cash balance available</p>
                    )}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="Monthly Burn"
                        value={inputs.burn}
                        onChange={(e) =>
                          handleNumberInput(
                            e.target.value,
                            'burn',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.burn &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      {validationWarnings.burn ? (
                        <AlertTriangle className="absolute right-3 top-3 h-4 w-4 text-yellow-500" />
                      ) : (
                        <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {validationWarnings.burn ? (
                      <p className="text-yellow-500 font-medium">{validationWarnings.burn}</p>
                    ) : (
                      <p>Monthly cash burn rate (negative cash flow)</p>
                    )}
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="DCF Years (3–5)"
                        type="number"
                        value={inputs.dcf_years}
                        onChange={(e) =>
                          setInputs({ ...inputs, dcf_years: Number(e.target.value || 5) })
                        }
                      />
                      <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Number of years to project cash flows (typically 3-5)
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Input
                        placeholder="Discount Rate %"
                        value={inputs.dcf_discount_pct}
                        onChange={(e) =>
                          handlePercentageInput(
                            e.target.value,
                            'dcf_discount_pct',
                            'Discount Rate',
                            setInputs,
                            setValidationWarnings,
                            saveToHistory
                          )
                        }
                        className={cn(
                          validationWarnings.dcf_discount_pct &&
                            'border-yellow-500 focus-visible:ring-yellow-500'
                        )}
                      />
                      <HelpCircle className="absolute right-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Risk-adjusted rate for discounting future cash (25% typical for early-stage)
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
            <div className="mt-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      onClick={runModels}
                      disabled={running || hasCriticalWarnings}
                      className={cn(hasCriticalWarnings && 'cursor-not-allowed')}
                    >
                      {running ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-1" />
                      )}
                      Run Models
                    </Button>
                  </div>
                </TooltipTrigger>
                {hasCriticalWarnings && (
                  <TooltipContent className="bg-yellow-500 text-white">
                    <p className="font-medium">Cannot run models with validation warnings</p>
                    <p className="text-xs mt-1">Fix the highlighted fields first</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </TabsContent>

          {/* Methods */}
          <TabsContent value="methods">
            {!result ? (
              <p className="text-sm text-muted-foreground">Run models to see results.</p>
            ) : (
              <>
                <div className="text-sm mb-4">
                  <div>
                    <strong>Valuation Range:</strong> {base} {num(result.range?.low)} —{' '}
                    {num(result.range?.high)} (mid: {num(result.range?.mid)})
                  </div>
                  <div className="mt-2 font-medium">Method Breakdown</div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Method</TableHead>
                      <TableHead>Assumptions</TableHead>
                      <TableHead className="text-right">Implied Valuation ({base})</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(result.methods || []).map((m: any) => (
                      <TableRow key={m.name}>
                        <TableCell>{m.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {m.assumptions || '—'}
                        </TableCell>
                        <TableCell className="text-right">{num(m.value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </TabsContent>

          {/* Offer Compare */}
          <TabsContent value="offer">
            <div className="grid sm:grid-cols-4 gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-medium">Offered Pre-Money</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-3 w-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>The pre-money valuation offered by the investor</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Input
                  placeholder="e.g., 5,000,000"
                  value={offer.offered_pre_money}
                  onChange={(e) =>
                    handleNumberInput(
                      e.target.value,
                      'offered_pre_money',
                      setOffer,
                      setValidationWarnings,
                      saveToHistory
                    )
                  }
                  className={cn(
                    validationWarnings.offered_pre_money &&
                      'border-yellow-500 focus-visible:ring-yellow-500'
                  )}
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-medium">Raise Amount</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-3 w-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Total amount of capital to be raised</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Input
                  placeholder="e.g., 1,000,000"
                  value={offer.raise_amount}
                  onChange={(e) =>
                    handleNumberInput(
                      e.target.value,
                      'raise_amount',
                      setOffer,
                      setValidationWarnings,
                      saveToHistory
                    )
                  }
                  className={cn(
                    validationWarnings.raise_amount &&
                      'border-yellow-500 focus-visible:ring-yellow-500'
                  )}
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-medium">Option Pool %</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-3 w-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Target percentage of the company allocated to employee stock options</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Input
                  placeholder="e.g., 10"
                  value={offer.pool_target_pct}
                  onChange={(e) =>
                    handlePercentageInput(
                      e.target.value,
                      'pool_target_pct',
                      'Option Pool',
                      setOffer,
                      setValidationWarnings,
                      saveToHistory
                    )
                  }
                  className={cn(
                    validationWarnings.pool_target_pct &&
                      'border-yellow-500 focus-visible:ring-yellow-500'
                  )}
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-medium">Instrument</label>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-3 w-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Type of security being offered</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Select
                  value={offer.instrument}
                  onValueChange={(v) => setOffer({ ...offer, instrument: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Instrument" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EQUITY">Equity</SelectItem>
                    <SelectItem value="SAFE">SAFE</SelectItem>
                    <SelectItem value="CONVERTIBLE">Convertible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    onClick={compareOffer}
                    disabled={hasOfferWarnings}
                    className={cn(hasOfferWarnings && 'cursor-not-allowed')}
                  >
                    Compare Offer
                  </Button>
                </div>
              </TooltipTrigger>
              {hasOfferWarnings && (
                <TooltipContent className="bg-yellow-500 text-white">
                  <p className="font-medium">Cannot compare with validation warnings</p>
                  <p className="text-xs mt-1">Fix the highlighted offer fields first</p>
                </TooltipContent>
              )}
            </Tooltip>
            {compare && (
              <div className="mt-4 border rounded p-3 text-sm space-y-2">
                <div>
                  <strong>Implied Post-Money:</strong> {base} {num(compare.post_money)}
                </div>
                <div>
                  <strong>Dilution (founders):</strong> {pct(compare.founder_dilution)}
                </div>
                <div>
                  <strong>Below/Above Mid:</strong> {pct(compare.delta_vs_mid)}
                </div>
                <div>
                  <strong>Red Flags:</strong>{' '}
                  {compare.flags?.length ? compare.flags.join('; ') : 'None'}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Negotiation */}
          <TabsContent value="negotiation">
            <div className="flex gap-2 mb-4">
              <Button size="sm" variant="outline" onClick={negotiationBrief}>
                <Sparkles className="h-4 w-4 mr-1" /> Draft Negotiation Brief
              </Button>
              <Button size="sm" onClick={exportBrief}>
                <Download className="h-4 w-4 mr-1" /> Export Valuation Brief
              </Button>
            </div>
            {brief && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="border rounded p-3">
                  <div className="font-medium text-sm mb-2">Counter-Offer Guidance</div>
                  <pre className="text-xs whitespace-pre-wrap text-muted-foreground">
                    {brief.counter_offer || '—'}
                  </pre>
                </div>
                <div className="border rounded p-3">
                  <div className="font-medium text-sm mb-2">Protection Checklist</div>
                  <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1">
                    {(brief.protections || []).map((p: string, i: number) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function num(n: any) {
  if (!n && n !== 0) return '—';
  const v = Number(n);
  return isNaN(v) ? '—' : v.toLocaleString();
}

function pct(n: any) {
  if (n == null) return '—';
  return `${(Number(n) * 100).toFixed(2)}%`;
}
